import React, { Suspense, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useLocation } from 'react-router-dom';
import { Layout, Spin, ConfigProvider } from 'antd';
import enUS from 'antd/locale/en_US';
import zhTW from 'antd/locale/zh_TW';
import viVN from 'antd/locale/vi_VN';
import { LoadingOutlined } from '@ant-design/icons';
import AuthContext from './contexts/authProvider';
import SettingContext from './contexts/settingProvider';
import Login from './pages/Login';
import SelectMenu from './pages/SelectMenu';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import Auth from './pages/Auth';
import { KnowledgeBaseProvider } from './contexts/knowledgeBaseContext';
import { ChatHistoryProvider } from './contexts/chatHistoryContext';
import { ProjectRoutesProvider } from './contexts/projectRoutesContext';
import { DocumentParserProvider } from './contexts/documentParserProvider';
import AIAssistant from './pages/usefulTools/aiAssistant';
import TierGuard from './components/TierGuard';
import { AI_ASSISTANT_TYPES } from './api/aiTools';
import { useTranslation } from 'react-i18next';
import { IS_STAGING } from './utils/config';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';
import 'dayjs/locale/en';
import 'dayjs/locale/vi';

const NotFound = React.lazy(() => import('./pages/NotFound'));
const FileManager = React.lazy(() => import('./pages/FileManager'));
const Project = React.lazy(() => import('./pages/Project'));
const Settings = React.lazy(() => import('./pages/Settings'));
const PersonalTokens = React.lazy(() => import('./pages/Settings/PersonalTokens'));
const AskQuestion = React.lazy(() => import('./pages/AskQuestion'));
const KnowledgeBaseManager = React.lazy(() => import('./pages/knowledgeBaseManager'));
const UsefulTools = React.lazy(() => import('./pages/usefulTools'));
const ChartTest = React.lazy(() => import('./pages/ChartTest'));
const DocumentParser = React.lazy(() => import('./pages/DocumentParser'));
const SchemaManager = React.lazy(() => import('./pages/DocumentParser/SchemaManager'));
const DataPipelineMonitor = React.lazy(() => import('./pages/DocumentParser/DataPipelineMonitor'));
const ServiceAccounts = React.lazy(() => import('./pages/Admin/ServiceAccounts'));
const AuditLogs = React.lazy(() => import('./pages/Admin/AuditLogs'));
const UserTiers = React.lazy(() => import('./pages/Admin/UserTiers'));

const antIcon = (
  <LoadingOutlined
    style={{
      fontSize: 48,
    }}
    spin
  />
);

function NavWrapper() {
  const { user } = useContext(AuthContext);
  const { dark } = useContext(SettingContext);
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  
  // Set dayjs locale based on i18n language
  React.useEffect(() => {
    switch (i18n.language) {
      case 'zh-TW':
        dayjs.locale('zh-tw');
        break;
      case 'en-US':
        dayjs.locale('en');
        break;
      case 'vi-VN':
        dayjs.locale('vi');
        break;
      default:
        dayjs.locale('zh-tw');
    }
  }, [i18n.language]);
  
  const getAntdLocale = () => {
    switch (i18n.language) {
      case 'en-US':
        return enUS;
      case 'vi-VN':
        return viVN;
      case 'zh-TW':
      default:
        return zhTW;
    }
  };
  
  const locale = getAntdLocale();

  if (!user) return <Navigate to="/login" />;

  if (pathname === '/projects') {
    return (
      <ConfigProvider locale={locale}>
        <Layout className={`app-layout ${dark ? 'dark' : ''}`}>
          <Header />
          <Suspense
            fallback={
              <div className="app-content loading" style={{ width: '100%' }}>
                <Spin indicator={antIcon} />
              </div>
            }
          >
            <Outlet />
          </Suspense>
          <Footer />
        </Layout>
      </ConfigProvider>
    );
  }

  if (pathname === '/selectmenu') {
    return (
      <ConfigProvider locale={locale}>
        <Layout className={`app-layout ${dark ? 'dark' : ''}`}>
          <Header />
          <Layout>
            <Layout>
              <Suspense
                fallback={
                  <div className="app-content loading" style={{ width: '100%' }}>
                    <Spin indicator={antIcon} />
                  </div>
                }
              >
                <Outlet />
              </Suspense>
              <Footer />
            </Layout>
          </Layout>
        </Layout>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider locale={locale}>
      <Layout className={`app-layout ${dark ? 'dark' : ''}`}>
        <Header />
        <Layout>
          <Sidebar />
          <Layout>
            <Suspense
              fallback={
                <div className="app-content loading" style={{ width: '100%' }}>
                  <Spin indicator={antIcon} />
                </div>
              }
            >
              <Outlet />
            </Suspense>
            {pathname.includes('/project') ? null : <Footer />}
          </Layout>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/" element={
          <ChatHistoryProvider>
            <ProjectRoutesProvider>
              <KnowledgeBaseProvider>
                <DocumentParserProvider>
                  <NavWrapper />
                </DocumentParserProvider>
              </KnowledgeBaseProvider>
            </ProjectRoutesProvider>
          </ChatHistoryProvider>
        }>
          <Route path="/" element={<Navigate replace to="/selectmenu" />} />
          <Route path="/selectmenu" element={<SelectMenu />} />
          {/* Redirect FileManager routes to homepage in STAGING environment */}
          <Route path="/filemanager" element={IS_STAGING ? <Navigate replace to="/selectmenu" /> : <FileManager />} />
          <Route path="/filemanager/:id" element={IS_STAGING ? <Navigate replace to="/selectmenu" /> : <FileManager />} />
          <Route path="/filemanager/folders/:id" element={IS_STAGING ? <Navigate replace to="/selectmenu" /> : <FileManager />} />
          <Route path="/project/dashboard" element={<Project />} />
          <Route path="/project/management" element={<Project />} />
          <Route path='/project/management/*' element={<Project />} />
          <Route path='/project/planning' element={<Project />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/tokens" element={<PersonalTokens />} />
          <Route path="/admin/service-accounts" element={<ServiceAccounts />} />
          <Route path="/admin/audit-logs" element={<AuditLogs />} />
          <Route path="/admin/user-tiers" element={<UserTiers />} />
          <Route path="/askquestion" element={<AskQuestion />} />
          <Route path="/askquestion/chat/:id" element={<AskQuestion />} />
          <Route path="/knowledgeBaseManager" element={<KnowledgeBaseManager />} />
          <Route path="/knowledgeBaseManager/:kb_id" element={<KnowledgeBaseManager />} />
          <Route path="/usefulTools" element={<UsefulTools />}>
            <Route path={AI_ASSISTANT_TYPES.MEETING_SUMMARY} element={<AIAssistant key={AI_ASSISTANT_TYPES.MEETING_SUMMARY} type={AI_ASSISTANT_TYPES.MEETING_SUMMARY} />} />
            <Route path={AI_ASSISTANT_TYPES.DOCUMENT_TRANSLATION} element={<AIAssistant key={AI_ASSISTANT_TYPES.DOCUMENT_TRANSLATION} type={AI_ASSISTANT_TYPES.DOCUMENT_TRANSLATION} />} />
            <Route path={AI_ASSISTANT_TYPES.SOLAR_CALCULATION} element={<TierGuard allowedTiers={['admin', 'developer']}><AIAssistant key={AI_ASSISTANT_TYPES.SOLAR_CALCULATION} type={AI_ASSISTANT_TYPES.SOLAR_CALCULATION} /></TierGuard>} />
          </Route>
          <Route path="/documentParser" element={<DocumentParser />} />
          <Route path="/documentParser/schemas" element={<SchemaManager />} />
          <Route path="/documentParser/monitor/:id" element={<DataPipelineMonitor />} />
          <Route path="/dev/chart-test" element={<ChartTest />} />
        </Route>
        <Route path="/*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;
